import { useState, useEffect, useMemo, useRef } from 'react';

const CORPORATE_EDIT_MIN_ROLE = 30;

/* Info tooltip — matches MainDashPage DashboardInfoTip (na-type-popover). */
const CorpDefaultInfoTip = () => {
    const [open, setOpen] = useState(false);
    const ref = useRef(null);
    const [tipStyle, setTipStyle] = useState({});

    const handleEnter = () => {
        if (!ref.current) return;
        const rect = ref.current.getBoundingClientRect();
        const spaceRight = window.innerWidth - rect.left;
        const spaceBelow = window.innerHeight - rect.bottom;

        setTipStyle({
            position: 'fixed',
            left: spaceRight < 320 ? undefined : `${rect.left}px`,
            right: spaceRight < 320 ? '16px' : undefined,
            top: spaceBelow < 120 ? undefined : `${rect.bottom + 6}px`,
            bottom: spaceBelow < 120 ? `${window.innerHeight - rect.top + 6}px` : undefined,
            maxWidth: '300px',
            whiteSpace: 'normal',
        });
        setOpen(true);
    };

    return (
        <span
            ref={ref}
            onMouseEnter={handleEnter}
            onMouseLeave={() => setOpen(false)}
            onFocus={handleEnter}
            onBlur={() => setOpen(false)}
            tabIndex={0}
            aria-label="What does corporate default mean?"
            style={{ cursor: 'help', marginLeft: '6px', color: 'var(--color-text-muted)', display: 'inline-flex', alignItems: 'center' }}
        >
            <i className="fe fe-info" style={{ fontSize: 'var(--font-size-sm)' }} aria-hidden="true" />
            {open ? (
                <span className="na-type-popover" style={tipStyle} role="tooltip">
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-muted)', fontWeight: 'var(--font-weight-semibold)' }}>
                        Corporate default
                    </span>
                    <span style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-body)', lineHeight: '1.45' }}>
                        Corporate-set reason codes automatically apply to every community under this corporation. Community-level users cannot edit them.
                    </span>
                </span>
            ) : null}
        </span>
    );
};

const CloudReasonDrawer = ({
    open,
    onClose,
    onSave,
    saving,
    globalReasons,
    reasonCodeTypes,
    roleWeight = 0,
    editTarget,
}) => {
    const [description, setDescription] = useState('');
    const [category, setCategory] = useState('');
    const [reasonTypeId, setReasonTypeId] = useState('');
    const [isCorporate, setIsCorporate] = useState(false);
    const [errors, setErrors] = useState({});

    const isEdit = !!editTarget;
    const targetIsCorporate = !!editTarget && editTarget.corporate_id != null && editTarget.corporate_id !== 0;
    const canEditCorporate = roleWeight > CORPORATE_EDIT_MIN_ROLE;
    // Form is locked when editing a corporate row without sufficient privilege.
    const locked = isEdit && targetIsCorporate && !canEditCorporate;

    // Reset form when drawer opens
    useEffect(() => {
        if (open) {
            if (editTarget) {
                setDescription(editTarget.reason_code_description || '');
                setCategory(editTarget.pal_reason_code_id?.toString() || '');
                // Prefer an explicit id from the row; fall back to looking up by name
                // since getReasonCodes may return reason_type_name without the id.
                let resolvedTypeId = editTarget.reason_code_type_id;
                if ((resolvedTypeId == null || resolvedTypeId === '') && editTarget.reason_type_name) {
                    const match = (reasonCodeTypes || []).find(
                        (t) => t.reason_type_name === editTarget.reason_type_name
                    );
                    resolvedTypeId = match?.reason_code_type_id;
                }
                setReasonTypeId(resolvedTypeId != null ? resolvedTypeId.toString() : '');
                setIsCorporate(editTarget.corporate_id != null && editTarget.corporate_id !== 0);
            } else {
                setDescription('');
                setCategory(globalReasons?.length > 0 ? globalReasons[0].pal_reason_id?.toString() : '');
                setReasonTypeId(reasonCodeTypes?.length > 0 ? reasonCodeTypes[0].reason_code_type_id?.toString() : '');
                setIsCorporate(false);
            }
            setErrors({});
        }
    }, [open, editTarget, globalReasons, reasonCodeTypes]);

    const validate = () => {
        const errs = {};
        if (!description.trim()) errs.description = 'Description is required.';
        if (!category) errs.category = 'Category is required.';
        if (!reasonTypeId) errs.reasonType = 'Reason type is required.';
        setErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleSave = () => {
        if (locked) return;
        if (!validate()) return;
        onSave({
            description: description.trim(),
            category: parseInt(category),
            reasonTypeId: parseInt(reasonTypeId),
            ...(isEdit ? { id: editTarget.cloud_reason_code_id } : { isCorporate }),
        });
    };

    const sortedCategories = useMemo(() => {
        return [...(globalReasons || [])].sort((a, b) =>
            (a.pal_reason_description || '').localeCompare(b.pal_reason_description || '')
        );
    }, [globalReasons]);

    const sortedTypes = useMemo(() => {
        return [...(reasonCodeTypes || [])].sort((a, b) =>
            (a.reason_type_name || '').localeCompare(b.reason_type_name || '')
        );
    }, [reasonCodeTypes]);

    return (
        <>
            {open ? <div className="dp-drawer-backdrop" onClick={onClose} /> : null}
            <div className={`dp-drawer${open ? ' is-open' : ''}`}>
                <div className="dp-drawer__header">
                    <h2 className="dp-drawer__title">
                        {isEdit ? 'Edit Reason Code' : 'Add Custom Reason'}
                    </h2>
                    <button className="dp-drawer__close" onClick={onClose}>
                        <i className="fe fe-x" />
                    </button>
                </div>
                <div className="dp-drawer__body">
                    {locked ? (
                        <div className="ss-inline-notice ss-inline-notice--warning">
                            <i className="fe fe-lock" aria-hidden="true" />
                            <span>This is a corporate-set reason code. Only corporate-level users can edit it.</span>
                        </div>
                    ) : null}

                    {isEdit ? (
                        <div className="dp-drawer__field">
                            <label className="dp-drawer__label">Scope</label>
                            <div className="ss-scope-readout">
                                <span className={`ss-scope-badge ss-scope-badge--${targetIsCorporate ? 'corporate' : 'community'}`}>
                                    {targetIsCorporate ? 'Corporate' : 'Community'}
                                </span>
                                <span className="ss-scope-readout__hint">
                                    {targetIsCorporate
                                        ? 'Applies to every community in this corporation.'
                                        : 'Applies to this community only.'}
                                </span>
                            </div>
                        </div>
                    ) : null}

                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Description <span className="dp-drawer__required">*</span>
                        </label>
                        <input
                            className={`dp-drawer__input${errors.description ? ' is-invalid' : ''}`}
                            type="text"
                            value={description}
                            placeholder="Enter reason description"
                            disabled={locked}
                            onChange={(e) => {
                                setDescription(e.target.value);
                                if (errors.description) setErrors((prev) => ({ ...prev, description: '' }));
                            }}
                        />
                        {errors.description ? (
                            <p className="dp-drawer__error">{errors.description}</p>
                        ) : null}
                    </div>

                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Category <span className="dp-drawer__required">*</span>
                        </label>
                        <select
                            className="dp-drawer__select"
                            value={category}
                            disabled={locked}
                            onChange={(e) => {
                                setCategory(e.target.value);
                                if (errors.category) setErrors((prev) => ({ ...prev, category: '' }));
                            }}
                        >
                            {sortedCategories.map((g) => (
                                <option key={g.pal_reason_id} value={g.pal_reason_id}>
                                    {g.pal_reason_description}
                                </option>
                            ))}
                        </select>
                        {errors.category ? (
                            <p className="dp-drawer__error">{errors.category}</p>
                        ) : null}
                    </div>

                    <div className="dp-drawer__field">
                        <label className="dp-drawer__label">
                            Reason Type <span className="dp-drawer__required">*</span>
                        </label>
                        <select
                            className="dp-drawer__select"
                            value={reasonTypeId}
                            disabled={locked}
                            onChange={(e) => {
                                setReasonTypeId(e.target.value);
                                if (errors.reasonType) setErrors((prev) => ({ ...prev, reasonType: '' }));
                            }}
                        >
                            <option value="">Select a reason type...</option>
                            {sortedTypes.map((t) => (
                                <option key={t.reason_code_type_id} value={t.reason_code_type_id}>
                                    {t.reason_type_name}
                                </option>
                            ))}
                        </select>
                        {errors.reasonType ? (
                            <p className="dp-drawer__error">{errors.reasonType}</p>
                        ) : null}
                    </div>

                    {!isEdit && canEditCorporate ? (
                        <div className="dp-drawer__field">
                            <label className="ss-corp-toggle">
                                <input
                                    type="checkbox"
                                    checked={isCorporate}
                                    onChange={(e) => setIsCorporate(e.target.checked)}
                                />
                                <span className="ss-corp-toggle__text">
                                    Set as corporate default
                                    <CorpDefaultInfoTip />
                                </span>
                            </label>
                        </div>
                    ) : null}
                </div>
                <div className="dp-drawer__footer">
                    <button className="dp-btn dp-btn--cancel" onClick={onClose}>
                        Cancel
                    </button>
                    <button
                        className="dp-btn dp-btn--primary"
                        disabled={saving || locked}
                        onClick={handleSave}
                        title={locked ? 'Corporate-set reason codes can only be edited by corporate-level users' : undefined}
                    >
                        {saving ? (isEdit ? 'Saving...' : 'Adding...') : (isEdit ? 'Save Changes' : 'Add Reason')}
                    </button>
                </div>
            </div>
        </>
    );
};

export default CloudReasonDrawer;
